import { useEffect, useRef } from 'react';
import { useVideosStore } from '../../state/videosStore';
import { runVideosApi } from '../../lib/runVideosApi';

interface Props {
  runId: string;
  slug: string;
}

/**
 * Thin HTML5 `<video>` wrapper. Two responsibilities:
 *   1. Stream from the per-run video endpoint so the player can scrub
 *      (the route uses HTTP range responses for this).
 *   2. Push the current playback second (floored to int) into the store
 *      via `timeupdate` so the right-pane analysis list can scroll the
 *      matching row into view.
 *
 * `key={slug}` upstream forces a remount when the user picks another
 * video so the browser drops the old buffer instead of trying to seek
 * across sources.
 */
export function VideoPlayer({ runId, slug }: Props) {
  const setCurrentSecond = useVideosStore((s) => s.setCurrentSecond);
  const ref = useRef<HTMLVideoElement>(null);
  const lastReported = useRef<number>(-1);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onTime = () => {
      const sec = Math.floor(el.currentTime);
      if (sec === lastReported.current) return;
      lastReported.current = sec;
      setCurrentSecond(sec);
    };
    el.addEventListener('timeupdate', onTime);
    return () => el.removeEventListener('timeupdate', onTime);
  }, [setCurrentSecond, slug]);

  // Reset the "last reported" guard on slug change so the new video gets
  // its first timeupdate echoed.
  useEffect(() => {
    lastReported.current = -1;
    setCurrentSecond(0);
  }, [slug, setCurrentSecond]);

  return (
    <video
      ref={ref}
      controls
      src={runVideosApi.streamUrl(runId, slug)}
      preload="metadata"
      style={{
        width: '100%',
        // Viewport-aware cap so the player can grow on bigger screens but
        // never crowds the per-frame list below on small ones.
        maxHeight: 'min(75vh, 720px)',
        background: '#000',
        borderRadius: 8,
        display: 'block',
      }}
    />
  );
}
